import {Patient} from "@/api/dto/client-profile";
import {Model} from "survey-react-ui";
import {Question} from "survey-core";
import {datePickerWidgetName} from "@common/survey_js/CustomDateWidget";
import {PrintFormStyles} from "@clients/profile_page/forms_tab/FormsTab.styles";
import FileHelpers from "@/helpers/file_helpers";
import DisplayHelpers from "@/helpers/display_helpers";
import {Form, FormCodeBinding} from "@/api/dto/forms";
import {addNewDiagnosis} from "@clients/profile_page/forms_tab/code_bindings/admission_discharge_crf_form";

const questionsToExclude = [
  'age_first_use_primary_input',
  'age_first_use_secondary_input',
  'age_first_use_tertiary_input',
  'addDiagnosisBtn'
];

export const printForm =
    ( templateName: string,
      surveyJs: Model,
      response: Record<string, unknown>,
      codeBinding?: FormCodeBinding,
      patient?: Patient)  => {

      if (response && codeBinding && isCrfForm(codeBinding)) {
        Object
          .keys(response)
          .filter(key => key.startsWith("diagnosis_"))
          .forEach((_, index) => {
            if (index > 2 && index < 11) {
              addNewDiagnosis(surveyJs, undefined, index + 1);
            }
          });
      }

      surveyJs.mergeData(response);
      const questions = surveyJs.getAllQuestions();
      const fullName = DisplayHelpers.formatName(patient?.firstName, patient?.middleName, patient?.lastName);

      const entries = questions
        .filter(question => !questionsToExclude.includes(question.name))
        .map((question: Question) => {
          if (question.customComponent === datePickerWidgetName) {
            const formatFn = isCrfForm(codeBinding)
              ? DisplayHelpers.formatDateFromDateOnlyString
              : DisplayHelpers.formatDate;
            question.value = question.value ? formatFn(question.value) : '';
          }
          question.value = question.value ?? '';

          return `
      <div class="question-block">
        <p class="heading"><strong>${question.title}:</strong></p> <p class="value">${question.displayValue}</p>
      </div>
    `;
        }).join('');

      const html = `
    <html lang="en">
      <head>
        <title> ${fullName}</title>
       ${PrintFormStyles}
      </head>
      <body>
        <h2>${fullName} - ${templateName}</h2>
        ${entries}
      </body>
    </html>
   `;

      const iframe = document.createElement('iframe');
      document.body.appendChild(iframe);
      iframe.style.display = 'none';
      iframe.id = 'pdf';

      const iframeDocument = iframe.contentWindow?.document;
      iframeDocument?.open();
      iframeDocument?.write(html);
      iframeDocument?.close();

      iframe.onload = function() {
        iframe.focus();
        iframe.contentWindow?.print();
        const removeIframe = () => {
          iframe.parentElement?.removeChild(iframe);
          window.removeEventListener('focus', removeIframe);
        };
        window.addEventListener('focus', removeIframe);
      };
    };

export const printUploadedForm = (form : Form, response: string)  => {
  const urlObject = getUrlObject(form, response);
  const pdfUrl = urlObject.formPdfUrl;
  const iframe = document.createElement('iframe');
  document.body.appendChild(iframe);
  iframe.style.display = 'none';
  iframe.src = pdfUrl;
  iframe.id = 'pdf';
  iframe.onload = function() {
    iframe.focus();
    iframe.contentWindow?.print();
    const removeIframe = () => {
      iframe.parentElement?.removeChild(iframe);
      window.removeEventListener('focus', removeIframe);
    };
    window.addEventListener('focus', removeIframe);
  };
  
  return {
    fileSize: urlObject.size
  };
};

export const downloadUploadedForm = (form: Form, response: string) => {
  const urlObject = getUrlObject(form, response);
  const pdfUrl = urlObject.formPdfUrl;

  const anchorTag = document.createElement('a');
  anchorTag.href = pdfUrl;
  anchorTag.target = '_blank';
  anchorTag.download = `${form.form_upload?.name}.pdf`;
  document.body.appendChild(anchorTag);
  anchorTag.click();
  document.body.removeChild(anchorTag);

  return {
    fileSize: urlObject.size
  };
};

export const getUrlObject = (form: Form, response: string) => {
  const arrayBuffer = FileHelpers.base64ToArrayBuffer(response);
  const blob = new Blob([arrayBuffer], {type: 'application/pdf'});
  return {
    form,
    formPdfUrl: URL.createObjectURL(blob),
    size: FileHelpers.formatBits(blob.size)
  };
};

export const isCrfForm = (codeBinding?: FormCodeBinding) =>
  codeBinding && [FormCodeBinding.DSAMH_CRF_ADMISSION_FORM, FormCodeBinding.DSAMH_CRF_DISCHARGE_FORM].includes(codeBinding);

